// Settles an unpaid expense in one step: the money went out, and the only things left
// to decide is WHOSE — one partner, or both with each one's part (migration 069).
//
// Deliberately NOT the edit form. The cost, its category and the date it was INCURRED
// were settled when the expense was recorded; paying it is a separate event that
// revises none of them — exactly as recording an installment against an invoice is not
// editing the invoice. One field, and the row it applies to shown above so the choice
// is never made against the wrong expense.
//
// NO DATE. The owners keep none for paying suppliers, so the server stamps `paid_on`
// with the day it is marked paid — the month the monthly cash flow puts this money in.
import { useState } from 'react';
import useAsync from '../hooks/useAsync';
import { useLookups } from '../context/LookupsContext';
import { expensesApi, authApi } from '../api';
import he from '../i18n/he';
import Modal from './Modal';
import { Cost } from './Money';
import { ErrorText } from './Loader';
import PayerSplitField from './PayerSplitField';
import { initialPayerState, payersPayload, splitAddsUp } from '../utils/payers';

export default function MarkPaidModal({ expense, onClose, onSaved }) {
  const { data: partners } = useAsync(() => authApi.partners());
  const lookups = useLookups();
  const [payerState, setPayerState] = useState(() => initialPayerState(null));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Falls back to the first partner so the visible selection and the value sent always
  // match — an empty <select> still shows its first option (utils/payers payersPayload).
  const list = partners || [];
  const ready = list.length > 0 && splitAddsUp(payerState, expense.amount, list);

  const submit = async () => {
    // The balance moves on this click, so a double click must not send it twice.
    if (saving) return;
    setSaving(true);
    try {
      await expensesApi.pay(expense.id, payersPayload(payerState, list));
      onSaved();
    } catch (e) {
      setError(e);
      setSaving(false);
    }
  };

  return (
    <Modal
      title={he.expenses.markPaidTitle}
      onClose={onClose}
      onSave={submit}
      saveLabel={he.expenses.markPaidConfirm}
      saveDisabled={!ready || saving}
    >
      <p className="w-modal-lead">
        <strong>{expense.description || lookups.categoryLabel(expense.category)}</strong>
        {' · '}
        <Cost value={expense.amount} />
      </p>
      <PayerSplitField
        label={he.expenses.whoPaid}
        partners={partners}
        amount={expense.amount}
        state={payerState}
        onChange={setPayerState}
      />
      <p className="muted small-note">{he.expenses.markPaidHint}</p>
      {error && <ErrorText error={error} />}
    </Modal>
  );
}
